import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { ElevatorBank } from '../../stacking-plan.model';
import {
  ReqCreateElevatorInfo,
  ReqDeleteElevators,
  ReqUpdateElevatorInfo,
  UpdateElevatorEdpRes,
} from '../actions-toolbar.model';
import { ActionsToolbarService } from '../actions-toolbar.service';
import { EDPElevatorBank } from '../../floor-container/elevator-banks/elevator-banks.model';
import { StackingPlanService } from '../../../stacking-plan.service';

@Injectable({ providedIn: 'root' })
export class ElevatorChangesHelperService {
  constructor(
    private stackingPlanService: StackingPlanService,
    private actionsToolbarService: ActionsToolbarService
  ) {}

  getElevatorRequests(elevatorBank, deletedElevators, elevatorInfo) {
    const elevatorRequests = [];
    if (deletedElevators != null && !!deletedElevators.length) {
      elevatorRequests.push(this.deleteElevatorBanks(deletedElevators));
    }

    if (elevatorBank != null) {
      const elevatorChanges = this.getUpdatedElevatorInfo(elevatorBank, elevatorInfo);
      if (!!elevatorChanges.length) {
        elevatorRequests.push(this.updateElevatorInfo(elevatorChanges, elevatorInfo));
      }
    }

    return elevatorRequests;
  }

  /**
   * User deleted floor's floor plan info will transform into required format
   * and will send data through API to update the changes
   */
  private deleteElevatorBanks(deletedElevators: ElevatorBank[]): Observable<any> {
    const deletedElevatorsInfo = deletedElevators.map(
      ({ elevatorBankId }) => new ReqDeleteElevators(this.stackingPlanService.propertyId, elevatorBankId)
    );
    return this.actionsToolbarService
      .deleteElevatorInfo(deletedElevatorsInfo)
      .pipe(this.actionsToolbarService.errorHandling('errorMessages.deleteElevatorBankInfo'));
  }

  /*
   * Checks for whether user has updated any elevator or not
   */
  private getUpdatedElevatorInfo(incomingElevatorBanks: EDPElevatorBank[], currentElevatorBanks: ElevatorBank[]) {
    return incomingElevatorBanks.filter(
      elevatorBank =>
        elevatorBank.id == null ||
        currentElevatorBanks.some(
          currentElevatorBank =>
            currentElevatorBank.elevatorBankId != null &&
            elevatorBank.id === currentElevatorBank.elevatorBankId &&
            this.checkElevatorUpdates(elevatorBank, currentElevatorBank)
        )
    );
  }

  private checkElevatorUpdates(elevatorBank: EDPElevatorBank, elevatorInfo: ElevatorBank) {
    return (
      elevatorInfo.elevatorTypeDesc !== elevatorBank.elevator_type_desc ||
      elevatorInfo.elevatorBankName !== elevatorBank.elevator_bank_name ||
      elevatorInfo.firstStop !== elevatorBank.start_floor ||
      elevatorInfo.lastStop !== elevatorBank.end_floor ||
      elevatorInfo.elevatorBankCapacity !== elevatorBank.elevator_bank_capacity ||
      elevatorInfo.elevatorBankCapacityUOM !== elevatorBank.elevator_bank_capacity_uom ||
      elevatorInfo.adaCompliant !== elevatorBank.ada_compliant_f ||
      elevatorInfo.elevatorBankNotes !== elevatorBank.elevator_bank_notes
    );
  }

  private getElevatorsChanges(elevatorChanges: EDPElevatorBank[], currentElevatorBank: ElevatorBank[]) {
    const elevatorsToUpdate: EDPElevatorBank[] = [];
    const elevatorsToCreate: EDPElevatorBank[] = [];

    /*
     * To make the unchanged/untouched values null and these null values will be
     * eliminated in ReqUpdateElevatorInfo
     */
    elevatorChanges.forEach(elevator => {
      if (elevator.id) {
        currentElevatorBank.forEach(currentElevator => {
          if (currentElevator.elevatorBankId === elevator.id) {
            const elevatorChange = this.getElevatorChangeObj(elevator, currentElevator);
            elevatorsToUpdate.push(elevatorChange);
          }
        });
      } else {
        elevatorsToCreate.push(elevator);
      }
    });

    return { elevatorsToUpdate, elevatorsToCreate };
  }

  /*
   * The User updated Elevator info will transform into required format and will
   * send data through API to update the changes
   */
  private updateElevatorInfo(elevatorChanges: EDPElevatorBank[], currentElevatorBank: ElevatorBank[]): Observable<any> {
    const { elevatorsToCreate, elevatorsToUpdate } = this.getElevatorsChanges(elevatorChanges, currentElevatorBank);

    const elevatorUpdates = elevatorsToUpdate.map(elevator => {
      return new ReqUpdateElevatorInfo(this.stackingPlanService.propertyId, elevator);
    });
    const elevatorCreations = elevatorsToCreate.map(elevator => {
      return new ReqCreateElevatorInfo(this.stackingPlanService.propertyId, elevator);
    });

    const elevatorRequests$: Array<Observable<UpdateElevatorEdpRes>> = [];

    if (elevatorUpdates.length > 0) {
      const req = elevatorUpdates.filter(elevator => Object.entries(elevator.property_elevator_bank).length > 0);
      const updateElevatorRequest$ = this.actionsToolbarService.updateElevatorInfoToEDP(req);
      elevatorRequests$.push(updateElevatorRequest$);
    }
    if (elevatorCreations.length > 0) {
      const createElevatorRequest$ = this.actionsToolbarService.createElevatorBank(elevatorCreations);
      elevatorRequests$.push(createElevatorRequest$);
    }

    return forkJoin(elevatorRequests$).pipe(
      this.actionsToolbarService.errorHandling('errorMessages.updateElevatorBankInfo')
    );
  }

  private getElevatorChangeObj(elevator: EDPElevatorBank, currentElevator: ElevatorBank) {
    return {
      id: elevator.id,
      elevator_type_desc:
        currentElevator.elevatorTypeDesc !== elevator.elevator_type_desc ? elevator.elevator_type_desc : null,
      elevator_bank_name:
        currentElevator.elevatorBankName !== elevator.elevator_bank_name ? elevator.elevator_bank_name : null,
      start_floor: currentElevator.firstStop !== elevator.start_floor ? elevator.start_floor : null,
      end_floor: currentElevator.lastStop !== elevator.end_floor ? elevator.end_floor : null,
      elevator_bank_capacity:
        currentElevator.elevatorBankCapacity !== elevator.elevator_bank_capacity
          ? elevator.elevator_bank_capacity
          : null,
      elevator_bank_capacity_uom:
        currentElevator.elevatorBankCapacityUOM !== elevator.elevator_bank_capacity_uom
          ? elevator.elevator_bank_capacity_uom
          : null,
      elevator_bank_notes:
        currentElevator.elevatorBankNotes !== elevator.elevator_bank_notes ? elevator.elevator_bank_notes : null,
      ada_compliant_f: currentElevator.adaCompliant !== elevator.ada_compliant_f ? elevator.ada_compliant_f : null,
    };
  }
}
